import { CEFR_LEVELS, type CefrLevel } from "@byfluent/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Alert, Button, Card, SelectInput, TextArea, TextInput } from "../../components/ui";
import { api, errorMessage } from "../../lib/api";

export interface ScenarioDraft {
  title: string;
  level: string;
  setting: string;
  fixedFacts: string[];
  aiPersona: string;
  studentRole: string;
  objectives: string[];
  targetVocabulary: { word: string; translation: string }[];
  grammarFocus: string;
  openingLine: string;
  voiceId: string;
  difficulty: number;
  summaryPt?: string | null;
}

const lines = (s: string) =>
  s
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

/** Edição do cenário antes de salvar (gerado, da biblioteca ou próprio). */
export function ScenarioEditor({
  studentId,
  scenarioId,
  initial,
  onDone,
}: {
  studentId: string;
  scenarioId?: string;
  initial: ScenarioDraft;
  onDone: () => void;
}) {
  const voices = useQuery({
    queryKey: ["voices"],
    queryFn: () => api.get<{ items: { id: string; name: string }[] }>("/v1/ai/voices"),
  });
  const [f, setF] = useState({
    title: initial.title,
    level: initial.level as CefrLevel,
    setting: initial.setting,
    fixedFacts: initial.fixedFacts.join("\n"),
    aiPersona: initial.aiPersona,
    studentRole: initial.studentRole,
    objectives: initial.objectives.join("\n"),
    vocabulary: initial.targetVocabulary.map((v) => `${v.word} = ${v.translation}`).join("\n"),
    grammarFocus: initial.grammarFocus,
    openingLine: initial.openingLine,
    voiceId: initial.voiceId,
    difficulty: String(initial.difficulty),
  });
  const content = () => ({
    title: f.title,
    level: f.level,
    setting: f.setting,
    fixedFacts: lines(f.fixedFacts),
    aiPersona: f.aiPersona,
    studentRole: f.studentRole,
    objectives: lines(f.objectives),
    targetVocabulary: lines(f.vocabulary).map((l) => {
      const [word, translation] = l.split("=").map((x) => x.trim());
      return { word: word ?? "", translation: translation ?? "" };
    }),
    grammarFocus: f.grammarFocus,
    openingLine: f.openingLine,
    voiceId: f.voiceId || voices.data?.items[0]?.id || "",
    difficulty: Number(f.difficulty),
  });
  const save = useMutation({
    mutationFn: () =>
      scenarioId
        ? api.patch(`/v1/scenarios/${scenarioId}`, { content: content() })
        : api.post(`/v1/students/${studentId}/scenarios`, {
            mode: "custom",
            content: content(),
            summaryPt: initial.summaryPt ?? undefined,
          }),
    onSuccess: onDone,
  });
  const set =
    (k: keyof typeof f) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setF({ ...f, [k]: e.target.value });
  return (
    <Card title={scenarioId ? "Editar cenário" : "Revisar cenário gerado"}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {initial.summaryPt ? <Alert kind="note">{initial.summaryPt}</Alert> : null}
        <div className="grid cols-2">
          <TextInput label="Título" value={f.title} onChange={set("title")} />
          <div className="grid cols-2">
            <SelectInput
              label="Nível"
              value={f.level}
              onChange={set("level")}
              options={CEFR_LEVELS.map((l) => ({ value: l, label: l }))}
            />
            <SelectInput
              label="Dificuldade"
              value={f.difficulty}
              onChange={set("difficulty")}
              options={[1, 2, 3, 4, 5].map((d) => ({ value: String(d), label: String(d) }))}
            />
          </div>
        </div>
        <TextArea label="Cenário (em inglês)" value={f.setting} onChange={set("setting")} />
        <TextArea
          label="Fatos fixos (um por linha)"
          hint="Preços, horários e nomes que o personagem nunca muda"
          value={f.fixedFacts}
          onChange={set("fixedFacts")}
        />
        <div className="grid cols-2">
          <TextInput label="Personagem da IA" value={f.aiPersona} onChange={set("aiPersona")} />
          <TextInput label="Papel do aluno" value={f.studentRole} onChange={set("studentRole")} />
        </div>
        <TextArea
          label="Objetivos (3 a 5, um por linha)"
          value={f.objectives}
          onChange={set("objectives")}
        />
        <TextArea
          label="Vocabulário (8 a 12, palavra = tradução)"
          value={f.vocabulary}
          onChange={set("vocabulary")}
          rows={6}
        />
        <div className="grid cols-2">
          <TextInput
            label="Foco gramatical"
            value={f.grammarFocus}
            onChange={set("grammarFocus")}
          />
          <SelectInput
            label="Voz do personagem"
            value={f.voiceId}
            onChange={set("voiceId")}
            options={(voices.data?.items ?? [{ id: f.voiceId, name: f.voiceId }]).map((v) => ({
              value: v.id,
              label: v.name,
            }))}
          />
        </div>
        <TextInput label="Fala de abertura" value={f.openingLine} onChange={set("openingLine")} />
        {save.error ? <Alert kind="risk">{errorMessage(save.error)}</Alert> : null}
        <div className="row">
          <Button type="submit" loading={save.isPending}>
            Salvar cenário
          </Button>
          <Button variant="ghost" onClick={onDone}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}
